import React, { useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@workspace/ui/components/Card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@workspace/ui/components/Table'
import { Badge } from '@workspace/ui/components/Badge'
import { Skeleton } from '@workspace/ui/components/Skeleton'
import { api, type Permission } from '@/shared/lib/api'
import { selectUser } from '@/shared/store/auth.store'
import { ScrollArea } from '@workspace/ui/components/ScrollArea'
import { Separator } from '@workspace/ui/components/Separator'

export const Route = createFileRoute('/dev/permissions')({
    component: UserPermissionsPage,
})

type GroupedPermissions = {
    [resource: string]: Permission[]
}

function UserPermissionsPage() {
    const user = selectUser()
    const [selectedResource, setSelectedResource] = useState<string | null>(null)

    // Fetch fresh user data with permissions
    const { data: currentUser, isLoading } = useQuery({
        queryKey: ['user', 'me'],
        queryFn: () => api.auth.getMe(),
        staleTime: 1000 * 60, // 1 minute
    })

    const permissions = currentUser?.permissions || []

    // Group permissions by resource
    const groupedPermissions = useMemo(() => {
        const grouped: GroupedPermissions = {}
        permissions.forEach(permission => {
            const resource = permission.resource || 'other'
            if (!grouped[resource]) {
                grouped[resource] = []
            }
            grouped[resource].push(permission)
        })
        return grouped
    }, [permissions])

    const resources = Object.keys(groupedPermissions).sort()

    // Filter permissions based on selected resource
    const filteredPermissions = useMemo(() => {
        if (!selectedResource) {
            return permissions
        }
        return groupedPermissions[selectedResource] || []
    }, [selectedResource, permissions, groupedPermissions])

    // Count permissions by type
    const stats = useMemo(() => {
        const systemCount = permissions.filter(p => p.isSystemPermission).length
        const regularCount = permissions.length - systemCount
        return { total: permissions.length, system: systemCount, regular: regularCount }
    }, [permissions])

    if (isLoading) {
        return (
            <div className="space-y-6">
                <div>
                    <h1 className="text-3xl font-bold">User Permissions</h1>
                    <p className="text-muted-foreground mt-2">Loading permissions...</p>
                </div>
                <Card>
                    <CardContent className="space-y-4 pt-6">
                        <Skeleton className="h-8 w-full" />
                        <Skeleton className="h-8 w-full" />
                        <Skeleton className="h-8 w-full" />
                    </CardContent>
                </Card>
            </div>
        )
    }

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-3xl font-bold">User Permissions</h1>
                <p className="text-muted-foreground mt-2">
                    Current permissions for <strong>{user?.fullName || currentUser?.fullName}</strong>
                </p>
            </div>

            {/* Stats Cards */}
            <div className="grid gap-4 md:grid-cols-3">
                <Card>
                    <CardHeader>
                        <CardTitle className="text-sm font-medium text-muted-foreground">Total Permissions</CardTitle>
                        <div className="text-2xl font-bold">{stats.total}</div>
                    </CardHeader>
                </Card>
                <Card>
                    <CardHeader>
                        <CardTitle className="text-sm font-medium text-muted-foreground">System Permissions</CardTitle>
                        <div className="text-2xl font-bold">{stats.system}</div>
                    </CardHeader>
                </Card>
                <Card>
                    <CardHeader>
                        <CardTitle className="text-sm font-medium text-muted-foreground">Regular Permissions</CardTitle>
                        <div className="text-2xl font-bold">{stats.regular}</div>
                    </CardHeader>
                </Card>
            </div>

            {/* User Info Card */}
            <Card>
                <CardHeader>
                    <CardTitle>User Information</CardTitle>
                    <CardDescription>Details about the current authenticated user</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid gap-3 text-sm">
                        <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Full Name:</span>
                            <span className="font-medium">{currentUser?.fullName}</span>
                        </div>
                        <Separator />
                        <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Email:</span>
                            <span className="font-medium">{currentUser?.email}</span>
                        </div>
                        <Separator />
                        <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Role:</span>
                            <Badge variant="outline">{currentUser?.role?.name || 'N/A'}</Badge>
                        </div>
                        <Separator />
                        <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Position:</span>
                            <span className="font-medium">{currentUser?.position || 'N/A'}</span>
                        </div>
                        <Separator />
                        <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Department:</span>
                            <span className="font-medium">{currentUser?.department?.name || 'N/A'}</span>
                        </div>
                        <Separator />
                        <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Status:</span>
                            <Badge variant={currentUser?.status === 'ACTIVE' ? 'success' : 'secondary'}>
                                {currentUser?.status}
                            </Badge>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Permissions Table Card */}
            <Card>
                <CardHeader>
                    <div className="flex flex-col gap-4">
                        <div>
                            <CardTitle>Permissions</CardTitle>
                            <CardDescription>
                                {selectedResource
                                    ? `Showing permissions for resource: ${selectedResource}`
                                    : 'All permissions across all resources'}
                            </CardDescription>
                        </div>

                        {/* Resource Filter Badges */}
                        {resources.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                <Badge
                                    variant={selectedResource === null ? 'default' : 'outline'}
                                    className="cursor-pointer"
                                    onClick={() => setSelectedResource(null)}
                                >
                                    All ({permissions.length})
                                </Badge>
                                {resources.map(resource => (
                                    <Badge
                                        key={resource}
                                        variant={selectedResource === resource ? 'default' : 'outline'}
                                        className="cursor-pointer"
                                        onClick={() => setSelectedResource(resource)}
                                    >
                                        {resource} ({groupedPermissions[resource]?.length || 0})
                                    </Badge>
                                ))}
                            </div>
                        )}
                    </div>
                </CardHeader>
                <CardContent>
                    {filteredPermissions.length === 0 ? (
                        <div className="py-8 text-center text-muted-foreground">
                            No permissions found for this user.
                        </div>
                    ) : (
                        <ScrollArea className="h-[600px]">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Display Name</TableHead>
                                        <TableHead>Resource</TableHead>
                                        <TableHead>Action</TableHead>
                                        <TableHead>Type</TableHead>
                                        <TableHead>Description</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredPermissions.map(permission => (
                                        <TableRow key={permission.id}>
                                            <TableCell className="font-mono text-xs">{permission.name}</TableCell>
                                            <TableCell className="font-medium">{permission.displayName}</TableCell>
                                            <TableCell>
                                                <Badge variant="secondary">{permission.resource}</Badge>
                                            </TableCell>
                                            <TableCell>
                                                <Badge
                                                    variant={permission.action === 'operate' ? 'default' : 'outline'}
                                                >
                                                    {permission.action}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {permission.isSystemPermission ? (
                                                    <Badge variant="destructive">System</Badge>
                                                ) : (
                                                    <Badge variant="success">Regular</Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-muted-foreground text-xs max-w-xs truncate">
                                                {permission.description || '—'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </ScrollArea>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}
